import React, { useEffect, useRef, useState } from "react";
import { UserAvatar } from "src/components/ui/avatar/Avatar";
import { useAppDispatch, useAppSelector } from "src/hooks/redux";
import { getItems } from "src/modules/spItems/actions";
import { selectItems } from "src/modules/spItems/selectors";
import { selectUsers } from "src/modules/users/selector";
import { TooltipItem } from "src/utils/Tooltip";
import {
  ContentPlanActionsProps,
  FilterKey,
  FilterMappings,
  FilterValue,
  SelectionFilterValue,
} from "../../../types/typesVPO";
import SortIcon from "src/components/ui-icons/table/SortIcon";
import { useSearchParams } from "react-router-dom";
import Loading from "src/components/Loading";
import { EmptyList } from "src/components/EmptyList";
import { myTaskMeetingsListName } from "src/api/config/meetings.config";

import {
  fetchVPOCombinedActions,
  clearVPOCombinedActionsData,
} from "../../../store/vpoCombinedActions/actions";
import {
  selectVPOCombinedActionsData,
  selectVPOCombinedActionsLoading,
  selectVPOCombinedActionsError,
  selectVPOCombinedActionsPagination,
  selectVPOCombinedActionsHasNextPage,
} from "../../../store/vpoCombinedActions/selectors";
import { siteUrl } from "src/api/config";

const itemsPerPage = 100;

const styles = {
  w_100:
    "px-6 py-3 w-[150px] font-semibold text-left border-b border-slate-200 text-sm",
  w_200:
    "px-6 py-3 w-[250px] font-semibold text-left border-b border-slate-200 text-sm",
  w_400:
    "px-6 py-3 w-[400px] font-semibold text-left border-b border-slate-200 text-sm",
  dataTable:
    "px-6 py-3 text-primary text-left border-b border-slate-200 text-sm",
};

const filterMappings: FilterMappings = {
  actions: {
    meeting: "Meeting",
    status: "Status",
  },
  vpo: {
    columnPlanAction: "ColumnID",
    columnBlock: "ColumnBlockID",
    subdivision: "Subdivision",
    process: "Process",
    demand: "DemandVPOColumns",
    groupPlan: "GroupID",
    factory: "FactoryID",
  },
};

const ContentPlanActions = React.memo<ContentPlanActionsProps>(
  ({ filters, onDataUpdate, pauseLoading = false }) => {
    const dispatch = useAppDispatch();
    const users: any = useAppSelector(selectUsers);
    const oneMeeting = useAppSelector((state) =>
      selectItems(state, myTaskMeetingsListName)
    );

    const vpoActionsData = useAppSelector(selectVPOCombinedActionsData);
    const loading = useAppSelector(selectVPOCombinedActionsLoading);
    const error = useAppSelector(selectVPOCombinedActionsError);
    const pagination = useAppSelector(selectVPOCombinedActionsPagination);
    const hasNextPage = useAppSelector(selectVPOCombinedActionsHasNextPage);

    const tableRef = useRef<HTMLDivElement>(null);
    const [sortOrderDate, setSortOrderDate] = useState<"asc" | "desc">("desc");
    const [sortOrderDoing, setSortOrderDoing] = useState<"asc" | "desc">("asc");
    const [prevFilters, setPrevFilters] = useState(filters);
    const [scrollPosition, setScrollPosition] = useState(0);
    const [query, setQuery] = useSearchParams();
    const [stableLoadingState, setStableLoadingState] = useState(false);
    const loadingTimeout = useRef<NodeJS.Timeout | null>(null);
    const [filterChangeCounter, setFilterChangeCounter] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);

    const getFormattedFilters = React.useCallback(() => {
      let sortBy = "ID";
      let sortOrder: "asc" | "desc" = "desc";

      if (sortOrderDate !== "desc") {
        sortBy = "created";
        sortOrder = sortOrderDate;
      } else if (sortOrderDoing !== "asc") {
        sortBy = "dateForDone";
        sortOrder = sortOrderDoing;
      }

      const apiParams: any = {
        page: currentPage,
        pageSize: itemsPerPage,
        sortBy,
        sortOrder,
      };

      if (filters.created?.startDate) {
        apiParams.createdStartDate = filters.created.startDate;
      }
      if (filters.created?.endDate) {
        apiParams.createdEndDate = filters.created.endDate;
      }
      if (filters.dataTable?.startDate) {
        apiParams.dateForDoneStartDate = filters.dataTable.startDate;
      }
      if (filters.dataTable?.endDate) {
        apiParams.dateForDoneEndDate = filters.dataTable.endDate;
      }
      if (filters.responsible?.selected?.length > 0) {
        apiParams.responsible = filters.responsible.selected;
      }

      function isFilterKey(key: string): key is FilterKey {
        return key in filters;
      }
      function isSelectionFilter(
        filter: FilterValue
      ): filter is SelectionFilterValue {
        return "selected" in filter;
      }

      //! Фильтры действий
      Object.entries(filterMappings.actions).forEach(
        ([filterKey, mappedKey]) => {
          if (
            isFilterKey(filterKey) &&
            filters[filterKey] &&
            isSelectionFilter(filters[filterKey]) &&
            filters[filterKey].selectedIds?.length > 0
          ) {
            const apiKey =
              filterKey === "status"
                ? "status"
                : filterKey === "meeting"
                  ? "meeting"
                  : filterKey;
            apiParams[apiKey] = filters[filterKey].selectedIds;
          }
        }
      );

      //! Фильтры VPO

      Object.entries(filterMappings.vpo).forEach(([filterKey, mappedKey]) => {
        if (
          isFilterKey(filterKey) &&
          filters[filterKey] &&
          isSelectionFilter(filters[filterKey]) &&
          filters[filterKey].selectedIds?.length > 0
        ) {
          apiParams[filterKey] = filters[filterKey].selectedIds;
        }
      });

      return apiParams;
    }, [filters, currentPage, sortOrderDate, sortOrderDoing]);

    useEffect(() => {
      const filtersChanged =
        JSON.stringify(filters) !== JSON.stringify(prevFilters);

      if (filtersChanged) {
        setPrevFilters(filters);
        setScrollPosition(0);
        setCurrentPage(1);
        setFilterChangeCounter((prev) => prev + 1);
        dispatch(clearVPOCombinedActionsData());
      }
    }, [filters, prevFilters, dispatch]);

    useEffect(() => {
      if (!pauseLoading && filterChangeCounter > 0) {
        const apiParams = getFormattedFilters();
        const isAppend = currentPage > 1;
        dispatch(fetchVPOCombinedActions(apiParams, isAppend));
      }
    }, [dispatch, filterChangeCounter, pauseLoading, getFormattedFilters]);

    useEffect(() => {
      if (!pauseLoading) {
        setFilterChangeCounter(1);
      }
    }, []);

    useEffect(() => {
      if (!loading && hasNextPage && !pauseLoading && currentPage > 0) {
        setCurrentPage((prev) => prev + 1);
        setFilterChangeCounter((prev) => prev + 1);
      }
    }, [loading, hasNextPage, pauseLoading, currentPage]);

    const toggleSortDate = () => {
      setSortOrderDate((prevOrder) => {
        const newOrder = prevOrder === "desc" ? "asc" : "desc";
        dispatch(clearVPOCombinedActionsData());
        setCurrentPage(1);
        setFilterChangeCounter((prev) => prev + 1);
        return newOrder;
      });
    };

    const toggleSortDoing = () => {
      setSortOrderDoing((prevOrder) => {
        const newOrder = prevOrder === "desc" ? "asc" : "desc";
        dispatch(clearVPOCombinedActionsData());
        setCurrentPage(1);
        setFilterChangeCounter((prev) => prev + 1);
        return newOrder;
      });
    };

    const openDocumentCard = (id: number) => {
      // const newSearchParams = new URLSearchParams(query);
      // newSearchParams.set("actionCard", id.toString());
      // setQuery(newSearchParams);
      window.open(`${siteUrl}/dashboard/3?actionCard=${id.toString()}`, "_blank");
    };

    const [meetingData, setMeetingData] = useState<any>(null);

    const openMeeting = (title: string) => {
      const baseUrl = siteUrl;

      dispatch(
        getItems({
          listName: myTaskMeetingsListName,
          filters: { Title: title },
        })
      );

      setMeetingData({ title, baseUrl });
    };

    useEffect(() => {
      if (meetingData && oneMeeting.length === 1) {
        const id = oneMeeting[0].ID;
        window.open(
          `${meetingData.baseUrl}/dashboard/3/meetings?meetingCard=${id}`,
          "_blank"
        );
        setMeetingData(null);
      }
    }, [oneMeeting, meetingData]);

    const actionsWithDateMethods = React.useMemo(() => {
      const levelDisplayToValue: Record<string, string> = { '0': 'Все' };
      const levelSelected = (filters.level?.selected || []).map(
        (v: string) => levelDisplayToValue[v] || v
      );
      const filteredData = levelSelected.length > 0
        ? vpoActionsData.filter((action: any) =>
          levelSelected.includes(action.DemandVPOColumns?.maturityLevel)
        )
        : vpoActionsData;

      return filteredData.map((action: any) => {
        const createdDate = new Date(action.Created);
        const dateForDoneDate = new Date(action.DateForDone);

        return {
          ...action,
          Created: {
            valueOf: () => createdDate.getTime(),
            format: (format: string) => {
              if (format === "DD.MM.YYYY") {
                return `${String(createdDate.getDate()).padStart(
                  2,
                  "0"
                )}.${String(createdDate.getMonth() + 1).padStart(
                  2,
                  "0"
                )}.${createdDate.getFullYear()}`;
              }
              return createdDate.toISOString();
            },
          },
          DateForDone: {
            valueOf: () => dateForDoneDate.getTime(),
            format: (format: string) => {
              if (format === "DD.MM.YYYY") {
                return `${String(dateForDoneDate.getDate()).padStart(
                  2,
                  "0"
                )}.${String(dateForDoneDate.getMonth() + 1).padStart(
                  2,
                  "0"
                )}.${dateForDoneDate.getFullYear()}`;
              }
              return dateForDoneDate.toISOString();
            },
          },
        };
      });
    }, [vpoActionsData]);

    useEffect(() => {
      if (tableRef.current && scrollPosition > 0) {
        tableRef.current.scrollTop = scrollPosition;
      }
    }, [actionsWithDateMethods, scrollPosition]);

    useEffect(() => {
      if (!pauseLoading) {
        onDataUpdate(actionsWithDateMethods);
      }
    }, [actionsWithDateMethods, onDataUpdate, pauseLoading]);

    useEffect(() => {
      if (loadingTimeout.current) {
        clearTimeout(loadingTimeout.current);
      }
      if (loading) {
        setStableLoadingState(true);
      } else {
        loadingTimeout.current = setTimeout(() => {
          setStableLoadingState(false);
        }, 500);
      }
      return () => {
        if (loadingTimeout.current) {
          clearTimeout(loadingTimeout.current);
        }
      };
    }, [loading]);

    // console.log('actionsWithDateMethods',actionsWithDateMethods)

    return (
      <div className="relative flex flex-col h-[calc(95vh-175px)] bg-white">
        <div
          ref={tableRef}
          className="flex-1 no-outer-borders w-[100%] overflow-x-auto overflow-y-auto scrollbar scrollbar-thumb-rounded-lg scrollbar-h-2 scrollbar-w-2 scrollbar-thumb-[#D1D5DB] rounded-md shadow mt-2"
        >
          <table className="w-[100%] table-fixed border-collapse">
            <thead className="text-s bg-white text-black sticky top-0 z-10">
              <tr className="border-b border-slate-200">
                <th scope="col" className={styles.w_100}>
                  Группа
                </th>
                <th scope="col" className={styles.w_100}>
                  Завод
                </th>
                <th scope="col" className={styles.w_200}>
                  Колонна
                </th>
                <th scope="col" className={styles.w_200}>
                  Блок колонны
                </th>
                <th scope="col" className={styles.w_200}>
                  Процесс
                </th>
                <th scope="col" className={styles.w_200}>
                  Уровень зрелости
                </th>
                <th scope="col" className={styles.w_400}>
                  Требования
                </th>
                <th scope="col" className={styles.w_200}>
                  Подразделение
                </th>
                <th
                  scope="col"
                  className={styles.w_200}
                  onClick={toggleSortDate}
                >
                  <span className="flex">
                    <span className="mr-3">Дата создания</span>
                    <SortIcon sortDirection={sortOrderDate} />
                  </span>
                </th>
                <th scope="col" className={styles.w_400}>
                  Несоответствие
                </th>
                <th scope="col" className={styles.w_400}>
                  Описание действия
                </th>
                <th scope="col" className={styles.w_200}>
                  Ответственный
                </th>
                <th
                  scope="col"
                  className={styles.w_200}
                  onClick={toggleSortDoing}
                >
                  <span className="flex">
                    <span className="mr-3">Срок выполнения</span>
                    <SortIcon sortDirection={sortOrderDoing} />
                  </span>
                </th>
                <th scope="col" className={styles.w_200}>
                  Статус
                </th>
                <th scope="col" className={styles.w_400}>
                  Название совещания
                </th>
              </tr>
            </thead>
            <tbody>
              {actionsWithDateMethods.length > 0 &&
                actionsWithDateMethods
                  .filter(
                    (action, index, self) =>
                      index === self.findIndex((a) => a.ID === action.ID)
                  )
                  .map((action) => (
                    <tr
                      key={action.ID}
                      className="bg-white hover:bg-slate-50 cursor-pointer h-[70px] border-b border-slate-200"
                      onClick={() => openDocumentCard(action.ID)}
                    >
                      <td className={styles.dataTable}>
                        {action.GroupTitle || "-"}
                      </td>
                      <td className={styles.dataTable}>
                        {action.FactoryTitle || "-"}
                      </td>
                      <td className={styles.dataTable}>{action.ColumnTitle}</td>
                      <td className={styles.dataTable}>
                        {action.ColumnBlockTitle}
                      </td>
                      <td className={styles.dataTable}>
                        {action.Process?.Title}
                      </td>
                      <td className={styles.dataTable}>
                        {action.DemandVPOColumns?.maturityLevel === 'Все' ? '0' : action.DemandVPOColumns?.maturityLevel}
                      </td>
                      <td className={styles.dataTable}>
                        {action.DemandVPOColumns?.Title}
                      </td>
                      <td className={styles.dataTable}>
                        {action.Subdivision?.Title}
                      </td>
                      <td className={styles.dataTable}>
                        {action.Created.format("DD.MM.YYYY")}
                      </td>
                      <td className={styles.dataTable}>
                        {action.Inconsistences
                          ? action.Inconsistences.replace(/<[^>]*>?/gm, "")
                          : ""}
                      </td>

                      <td className={styles.dataTable}>
                        <div
                          dangerouslySetInnerHTML={{
                            __html: action.Description.replace(
                              /(^|\W)(#[а-я\w][а-я\w-]*)/gi,
                              '<span style="color: rgb(0,98,155)">$&</span>'
                            ),
                          }}
                        />
                      </td>
                      <td className={`${styles.dataTable}`}>
                        <div className="flex items-center justify-between w-full">
                          <span className="flex items-center">
                            <TooltipItem
                              className="-translate-x-1"
                              position="top-right"
                              tooltipsText={
                                users[action.Responsible.Email]?.displayName ||
                                action.Responsible.Name ||
                                action.Responsible.Email
                              }
                            >
                              <UserAvatar
                                avatar={
                                  users[action.Responsible.Email]?.avatar ||
                                  null
                                }
                                shortName={
                                  users[action.Responsible.Email]?.shortName ||
                                  action.Responsible.Name ||
                                  action.Responsible.Email
                                }
                                displayName={
                                  users[action.Responsible.Email]
                                    ?.displayName ||
                                  action.Responsible.Name ||
                                  action.Responsible.Email
                                }
                                email={action.Responsible.Email}
                              />
                            </TooltipItem>
                            <span className="ml-6">
                              {action.Responsible.Name ||
                                action.Responsible.Email}
                            </span>
                          </span>
                        </div>
                      </td>
                      <td className={styles.dataTable}>
                        {action.DateForDone.format("DD.MM.YYYY")}
                      </td>
                      <td className={styles.dataTable}>
                        <div className="flex items-center">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="10"
                            height="10"
                            viewBox="0 0 10 10"
                            fill="none"
                          >
                            <circle
                              cx="5"
                              cy="5"
                              r="5"
                              fill={action.Status_x003a_Color.Title}
                            />
                          </svg>
                          <span
                            className="ml-2"
                            style={{ color: action.Status_x003a_Color.Title }}
                          >
                            {action.Status?.Title || "-"}
                          </span>
                        </div>
                      </td>
                      <td
                        className={`font-normal !text-[rgb(5,37,122)] ${styles.dataTable}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          openMeeting(action.Meeting.Title);
                        }}
                      >
                        {action.Meeting.Title}
                      </td>
                    </tr>
                  ))}

              {/* {(isInitialLoading || isLoadingMore) && (
              <tr>
                <td colSpan={4} className='text-center py-4'>
                  <div className='flex items-center justify-center'>
                    <Loading />
                  </div>
                </td>
              </tr>
            )} */}
              {!loading && actionsWithDateMethods.length === 0 && (
                <tr>
                  <td colSpan={4}>
                    <EmptyList title="Действия не найдены" />
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        {stableLoadingState && (
          <div className="flex justify-center w-full bg-[#E5E7EB] text-black text-center py-2">
            <span className="mr-2">
              <Loading />
            </span>
            Формируется список, пожалуйста подождите
          </div>
        )}

        {actionsWithDateMethods.length > 0 &&
          !hasNextPage &&
          !stableLoadingState && (
            <div className="w-full bg-[#012169] text-white text-center py-2">
              Список сформирован
            </div>
          )}
      </div>
    );
  }
);

export default ContentPlanActions;
